import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Ticket,
  RefreshCw,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Mail,
  Phone,
  User,
  Calendar,
  ArrowRight,
  Send,
  UserCheck,
  ChevronDown,
  MoreHorizontal,
  ExternalLink,
  Tag
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';

/**
 * AI Tickets & Escalation Page
 * View and manage support tickets created through AI escalation
 * Phase 1: Enhanced with assigned admin and quick reply panel
 */

const AITicketsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quickReply, setQuickReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [assigningAdmin, setAssigningAdmin] = useState(false);

  // Mock admin list
  const mockAdmins = [
    { id: '1', name: 'John Admin', email: 'john@fundelite.com', avatar: 'JA' },
    { id: '2', name: 'Sarah Support', email: 'sarah@fundelite.com', avatar: 'SS' },
    { id: '3', name: 'Mike Manager', email: 'mike@fundelite.com', avatar: 'MM' }
  ];

  // Mock tickets data
  const mockTickets = [
    {
      _id: 'ticket_1',
      subject: 'Cannot access credit report after import',
      description: 'I imported my credit report from IdentityIQ but the system shows "Processing" for over 2 hours. The AI assistant could not help resolve this issue.',
      email: 'client1@example.com',
      phone: '+1 (555) 123-4567',
      status: 'open',
      priority: 'high',
      category: 'technical',
      source: 'ai_assistant',
      assigned_to: null,
      created_at: new Date(Date.now() - 3600000).toISOString(),
      conversation: [
        { role: 'user', message: 'My credit report has been processing for 2 hours', timestamp: new Date(Date.now() - 7200000).toISOString() },
        { role: 'assistant', message: 'I understand your concern. Let me check on that...', timestamp: new Date(Date.now() - 7100000).toISOString() },
        { role: 'user', message: 'It still shows processing', timestamp: new Date(Date.now() - 3700000).toISOString() },
        { role: 'system', message: 'Ticket escalated to human support', timestamp: new Date(Date.now() - 3600000).toISOString() }
      ],
      tags: ['import', 'processing', 'urgent']
    },
    {
      _id: 'ticket_2',
      subject: 'Question about dispute letter templates',
      description: 'I want to customize the dispute letter template but AI could not explain how to do it. Need human guidance.',
      email: 'client2@example.com',
      phone: null,
      status: 'in_progress',
      priority: 'normal',
      category: 'feature',
      source: 'ai_assistant',
      assigned_to: { id: '1', name: 'John Admin', avatar: 'JA' },
      created_at: new Date(Date.now() - 86400000).toISOString(),
      conversation: [
        { role: 'user', message: 'How do I customize dispute letters?', timestamp: new Date(Date.now() - 86500000).toISOString() },
        { role: 'assistant', message: 'You can find templates in the Dispute Center...', timestamp: new Date(Date.now() - 86400000).toISOString() }
      ],
      tags: ['templates', 'disputes']
    },
    {
      _id: 'ticket_3',
      subject: 'Billing question - subscription upgrade',
      description: 'Want to upgrade my subscription but the AI said it cannot process billing changes.',
      email: 'client3@example.com',
      phone: '+1 (555) 987-6543',
      status: 'resolved',
      priority: 'normal',
      category: 'billing',
      source: 'ai_assistant',
      assigned_to: { id: '2', name: 'Sarah Support', avatar: 'SS' },
      created_at: new Date(Date.now() - 172800000).toISOString(),
      resolved_at: new Date(Date.now() - 86400000).toISOString(),
      conversation: [
        { role: 'user', message: 'I want to upgrade to Pro plan', timestamp: new Date(Date.now() - 172900000).toISOString() },
        { role: 'assistant', message: 'For billing changes, let me connect you with our team...', timestamp: new Date(Date.now() - 172800000).toISOString() }
      ],
      tags: ['billing', 'upgrade'],
      resolution: 'Upgraded customer to Pro plan. Applied 10% loyalty discount.'
    },
    {
      _id: 'ticket_4',
      subject: 'App not loading on mobile',
      description: 'The dashboard does not load properly on my iPhone. Just shows a blank screen.',
      email: 'client4@example.com',
      phone: null,
      status: 'open',
      priority: 'urgent',
      category: 'technical',
      source: 'ai_assistant',
      assigned_to: null,
      created_at: new Date(Date.now() - 1800000).toISOString(),
      conversation: [],
      tags: ['mobile', 'bug']
    },
    {
      _id: 'ticket_5',
      subject: 'Need refund for last month',
      description: 'I was charged but did not use the service. AI said it cannot process refunds.',
      email: 'client5@example.com',
      phone: '+1 (555) 456-7890',
      status: 'closed',
      priority: 'normal',
      category: 'billing',
      source: 'ai_assistant',
      assigned_to: { id: '2', name: 'Sarah Support', avatar: 'SS' },
      created_at: new Date(Date.now() - 604800000).toISOString(),
      resolved_at: new Date(Date.now() - 518400000).toISOString(),
      conversation: [],
      tags: ['refund', 'billing'],
      resolution: 'Refund processed. Customer account credited.'
    }
  ];

  // Quick reply templates
  const replyTemplates = [
    { label: 'Acknowledge', text: 'Thank you for reaching out. I\'m looking into this issue and will get back to you shortly.' },
    { label: 'Need More Info', text: 'To help resolve this issue, could you please provide more details about the problem?' },
    { label: 'In Progress', text: 'I\'m actively working on your request. I\'ll update you as soon as I have more information.' },
    { label: 'Resolved', text: 'Great news! I\'ve resolved the issue you reported. Please let me know if you have any other questions.' }
  ];

  useEffect(() => {
    fetchTickets();
  }, [filter]);

  const fetchTickets = async () => {
    try {
      let url = '/api/admin/ai/tickets?limit=100';
      if (filter !== 'all') {
        url += `&status=${filter}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets?.length > 0 ? data.tickets : mockTickets);
      } else {
        setTickets(mockTickets);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      setTickets(mockTickets);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    toast.success(`Status updated to ${newStatus} (Simulated)`);
    setTickets(prev => prev.map(t =>
      t._id === ticketId ? { ...t, status: newStatus } : t
    ));
    if (selectedTicket?._id === ticketId) {
      setSelectedTicket(prev => ({ ...prev, status: newStatus }));
    }
  };

  const assignAdmin = async (ticketId, admin) => {
    setAssigningAdmin(true);
    setTimeout(() => {
      toast.success(`Assigned to ${admin.name} (Simulated)`);
      setTickets(prev => prev.map(t =>
        t._id === ticketId ? { ...t, assigned_to: admin, status: t.status === 'open' ? 'in_progress' : t.status } : t
      ));
      if (selectedTicket?._id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, assigned_to: admin, status: prev.status === 'open' ? 'in_progress' : prev.status }));
      }
      setAssigningAdmin(false);
    }, 500);
  };

  const sendQuickReply = async () => {
    if (!quickReply.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    setTimeout(() => {
      toast.success('Reply sent (Simulated)');
      const newMessage = {
        role: 'admin',
        message: quickReply,
        timestamp: new Date().toISOString(),
        admin: user?.name || 'Admin'
      };
      setSelectedTicket(prev => ({
        ...prev,
        conversation: [...(prev.conversation || []), newMessage]
      }));
      setQuickReply('');
      setSendingReply(false);
    }, 500);
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      in_progress: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      resolved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      closed: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return colors[status] || colors.open;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="w-3.5 h-3.5" />,
      in_progress: <Clock className="w-3.5 h-3.5" />,
      resolved: <CheckCircle className="w-3.5 h-3.5" />,
      closed: <XCircle className="w-3.5 h-3.5" />
    };
    return icons[status] || icons.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      normal: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      high: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      urgent: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[priority] || colors.normal;
  };

  const getCategoryColor = (category) => {
    const colors = {
      technical: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      billing: 'bg-green-500/20 text-green-300 border-green-500/30',
      feature: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      general: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
    };
    return colors[category] || colors.general;
  };

  const statusOptions = [
    { value: 'all', label: 'All Tickets' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' }
  ];

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const ticketCounts = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shadow-lg shadow-rose-500/20">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            Tickets & Escalation
          </h1>
          <p className="text-slate-400 mt-1">
            Support tickets created through AI Assistant escalation
          </p>
        </div>
        <Button
          onClick={fetchTickets}
          variant="outline"
          size="sm"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Open</p>
                <p className="text-2xl font-bold text-amber-400">{ticketCounts.open}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">In Progress</p>
                <p className="text-2xl font-bold text-blue-400">{ticketCounts.in_progress}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Resolved</p>
                <p className="text-2xl font-bold text-emerald-400">{ticketCounts.resolved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Closed</p>
                <p className="text-2xl font-bold text-slate-400">{ticketCounts.closed}</p>
              </div>
              <XCircle className="w-8 h-8 text-slate-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tickets..."
            className="pl-10 bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {statusOptions.map((status) => (
            <Button
              key={status.value}
              variant={filter === status.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status.value)}
              className={filter === status.value 
                ? 'bg-emerald-600 text-white' 
                : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
            >
              {status.label}
              {status.value !== 'all' && (
                <span className="ml-1 text-xs opacity-70">
                  ({ticketCounts[status.value] || 0})
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Tickets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-2">
          <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">
                Tickets ({filteredTickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-400">No tickets found.</p>
                  <p className="text-sm text-slate-500 mt-1">Tickets will appear here when users escalate through AI Assistant.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTickets.map((ticket) => (
                    <div
                      key={ticket._id}
                      onClick={() => setSelectedTicket(ticket)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedTicket?._id === ticket._id
                          ? 'border-emerald-500/50 bg-emerald-500/5'
                          : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${getStatusColor(ticket.status)} border text-xs flex items-center gap-1`}>
                            {getStatusIcon(ticket.status)}
                            {ticket.status?.replace('_', ' ')}
                          </Badge>
                          <Badge className={`${getPriorityColor(ticket.priority)} border text-xs`}>
                            {ticket.priority}
                          </Badge>
                          <Badge className={`${getCategoryColor(ticket.category)} border text-xs`}>
                            {ticket.category}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500">
                          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <h3 className="font-medium text-white line-clamp-1">
                        {ticket.subject}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                        {ticket.description}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {ticket.email}
                          </span>
                          {ticket.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {ticket.phone}
                            </span>
                          )}
                        </div>
                        {ticket.assigned_to && (
                          <Badge className="bg-slate-700 text-slate-300 border-0 text-xs flex items-center gap-1">
                            <UserCheck className="w-3 h-3" />
                            {ticket.assigned_to.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ticket Details Panel */}
        <div className="space-y-4">
          {selectedTicket ? (
            <>
              {/* Ticket Info */}
              <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">Ticket Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${getStatusColor(selectedTicket.status)} border`}>
                      {getStatusIcon(selectedTicket.status)}
                      <span className="ml-1">{selectedTicket.status?.replace('_', ' ')}</span>
                    </Badge>
                    <Badge className={`${getPriorityColor(selectedTicket.priority)} border`}>
                      {selectedTicket.priority}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500">Subject</p>
                    <p className="font-medium text-white">{selectedTicket.subject}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="text-sm text-slate-300">{selectedTicket.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="text-sm text-slate-300">{selectedTicket.email}</p>
                    </div>
                    {selectedTicket.phone && (
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="text-sm text-slate-300">{selectedTicket.phone}</p>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {selectedTicket.tags?.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Tags</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedTicket.tags.map((tag, idx) => (
                          <Badge key={idx} className="bg-slate-700 text-slate-300 text-xs flex items-center gap-1">
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="text-sm text-slate-300">
                      {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>

                  {selectedTicket.resolution && (
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <p className="text-xs text-emerald-400 mb-1">Resolution</p>
                      <p className="text-sm text-emerald-300">{selectedTicket.resolution}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Assigned Admin */}
              <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-slate-300">
                    <UserCheck className="w-4 h-4 text-blue-400" />
                    Assigned Admin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedTicket.assigned_to ? (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-300 text-xs font-medium">
                          {selectedTicket.assigned_to.avatar}
                        </div>
                        <div>
                          <p className="font-medium text-white text-sm">{selectedTicket.assigned_to.name}</p>
                          <p className="text-xs text-slate-500">Assigned</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-400 hover:text-white"
                        onClick={() => {/* Reassign modal */}}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-slate-500">No admin assigned</p>
                      <div className="space-y-2">
                        {mockAdmins.map((admin) => (
                          <button
                            key={admin.id}
                            onClick={() => assignAdmin(selectedTicket._id, admin)}
                            disabled={assigningAdmin}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 text-xs font-medium">
                              {admin.avatar}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-white">{admin.name}</p>
                              <p className="text-xs text-slate-500">{admin.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Reply Panel */}
              <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-slate-300">
                    <MessageSquare className="w-4 h-4 text-emerald-400" />
                    Quick Reply
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Template Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {replyTemplates.map((template, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickReply(template.text)}
                        className="border-slate-600 text-slate-300 text-xs hover:bg-slate-800"
                      >
                        {template.label}
                      </Button>
                    ))}
                  </div>

                  {/* Reply Input */}
                  <Textarea
                    value={quickReply}
                    onChange={(e) => setQuickReply(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="bg-slate-800/50 border-slate-700 text-white text-sm"
                  />
                  <Button
                    onClick={sendQuickReply}
                    disabled={!quickReply.trim() || sendingReply}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {sendingReply ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Reply
                  </Button>
                </CardContent>
              </Card>

              {/* Status Update */}
              <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">Update Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                      <Button
                        key={status}
                        variant={selectedTicket.status === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateTicketStatus(selectedTicket._id, status)}
                        className={selectedTicket.status === status 
                          ? 'bg-emerald-600 text-white' 
                          : 'border-slate-600 text-slate-300 text-xs'}
                      >
                        {status.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Conversation History */}
              {selectedTicket.conversation?.length > 0 && (
                <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Conversation History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedTicket.conversation.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-2 rounded-lg text-xs ${
                            msg.role === 'user'
                              ? 'bg-slate-800/50 text-slate-300'
                              : msg.role === 'admin'
                                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                                : msg.role === 'system'
                                  ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                                  : 'bg-blue-500/10 text-blue-300 border border-blue-500/20'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium capitalize">{msg.role}</span>
                            <span className="text-slate-500">
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                            </span>
                          </div>
                          <p>{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">Select a ticket to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AITicketsPage;
